import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string;
  error?: string;
  statusCode?: number;
}

export interface StampVendorItem {
  name: string;
  phone: string;
  isDefault?: boolean;
}

export interface PaymentDetails {
  gpayPhonepe: string;
  accountHolder: string;
  accountNo: string;
  bank: string;
  branch: string;
  ifscCode: string;
}

export interface AppSettingsResponse {
  govtFeeOptions: number[];
  profFee: number;
  discussionProfFeeLegalAdvicePerApp: number;
  discussionProfFeeFilingPerApp: number;
  discussionProfFeeFollowUpPerApp: number;
  govtFeeToExpeditedFee: Record<number, number>;
  paymentDetails: PaymentDetails;
  stampVendors?: StampVendorItem[];
  stampPaperRateUa?: number;
  stampPaperRatePoA?: number;
  stampPaperRateHearing?: number;
  notaryRatePerStampUa?: number;
  notaryRateHearing?: number;
  replyToObjectionReplyPerApp?: number;
  replyToObjectionTmMPerApp?: number;
  hearingInvoiceAmountPerApp?: number;
  tmMBeforeHearingAmountPerApp?: number;
  tmMAfterHearingAmountPerApp?: number;
  subsequentHearingAmountPerApp?: number;
  counterGovtFeePerApp?: number;
  counterProfFeePerApp?: number;
  oppositionGovtFeePerApp?: number;
  oppositionProfFeePerApp?: number;
  oppositionTmMBeforeHearingAmountPerApp?: number;
  evidenceProfFeePerApp?: number;
  rejoinderProfFeePerApp?: number;
  oppositionHearingProfFeePerApp?: number;
  tmMBeforeOppositionHearingAmountPerApp?: number;
  tmMAfterOppositionHearingAmountPerApp?: number;
  subsequentOppositionHearingAmountPerApp?: number;
  followUpFilingChargesPerApp?: number;
  followUpProcessingChargesPerApp?: number;
  assignmentDeedPrepPerApp?: number;
  assignmentTmpOwnershipPerApp?: number;
  assignmentTmpAttorneyPerApp?: number;
  assignmentPoaPrepPerApp?: number;
  assignmentNocPrepPerApp?: number;
  assignmentAffidavitPrepPerApp?: number;
  assignmentSofPrepPerApp?: number;
  renewalProfFeePerApp?: number;
  renewalGovtFeePerApp?: number;
  legalCertProfFeePerApp?: number;
  legalCertGovtFeePerApp?: number;
  surveillanceProfFeePerApp?: number;
}

export interface UpdateAppSettingsPayload {
  govtFeeOptions?: number[];
  profFee?: number;
  discussionProfFeeLegalAdvicePerApp?: number;
  discussionProfFeeFilingPerApp?: number;
  discussionProfFeeFollowUpPerApp?: number;
  govtFeeToExpeditedFee?: Record<number, number>;
  gpayPhonepe?: string;
  accountHolder?: string;
  accountNo?: string;
  bank?: string;
  branch?: string;
  ifscCode?: string;
  stampVendors?: StampVendorItem[];
  stampPaperRateUa?: number;
  stampPaperRatePoA?: number;
  stampPaperRateHearing?: number;
  notaryRatePerStampUa?: number;
  notaryRateHearing?: number;
  replyToObjectionReplyPerApp?: number;
  replyToObjectionTmMPerApp?: number;
  hearingInvoiceAmountPerApp?: number;
  tmMBeforeHearingAmountPerApp?: number;
  tmMAfterHearingAmountPerApp?: number;
  subsequentHearingAmountPerApp?: number;
  counterGovtFeePerApp?: number;
  counterProfFeePerApp?: number;
  oppositionGovtFeePerApp?: number;
  oppositionProfFeePerApp?: number;
  oppositionTmMBeforeHearingAmountPerApp?: number;
  evidenceProfFeePerApp?: number;
  rejoinderProfFeePerApp?: number;
  oppositionHearingProfFeePerApp?: number;
  tmMBeforeOppositionHearingAmountPerApp?: number;
  tmMAfterOppositionHearingAmountPerApp?: number;
  subsequentOppositionHearingAmountPerApp?: number;
  followUpFilingChargesPerApp?: number;
  followUpProcessingChargesPerApp?: number;
  assignmentDeedPrepPerApp?: number;
  assignmentTmpOwnershipPerApp?: number;
  assignmentTmpAttorneyPerApp?: number;
  assignmentPoaPrepPerApp?: number;
  assignmentNocPrepPerApp?: number;
  assignmentAffidavitPrepPerApp?: number;
  assignmentSofPrepPerApp?: number;
  renewalProfFeePerApp?: number;
  renewalGovtFeePerApp?: number;
  legalCertProfFeePerApp?: number;
  legalCertGovtFeePerApp?: number;
  surveillanceProfFeePerApp?: number;
}

export async function getAppSettings(): Promise<AppSettingsResponse> {
  const res = await apiFetch(`${API_BASE}/app-settings`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<AppSettingsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load app settings";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<AppSettingsResponse>).data;
}

export async function updateAppSettings(
  payload: UpdateAppSettingsPayload
): Promise<AppSettingsResponse> {
  const res = await apiFetch(`${API_BASE}/app-settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<AppSettingsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update app settings";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<AppSettingsResponse>).data;
}

export interface AppSettingsHistoryRow {
  id: string;
  action: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ipAddress: string | null;
  correlationId: string | null;
  createdAt: string;
}

export interface AppSettingsHistoryResponse {
  data: AppSettingsHistoryRow[];
  total: number;
  page: number;
  limit: number;
}

export interface AppSettingsHistoryQuery {
  page?: number;
  limit?: number;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function getAppSettingsHistory(
  query: AppSettingsHistoryQuery = {}
): Promise<AppSettingsHistoryResponse> {
  const params = new URLSearchParams();
  if (query.page) params.set("page", String(query.page));
  if (query.limit) params.set("limit", String(query.limit));
  if (query.userId) params.set("userId", query.userId);
  if (query.dateFrom) params.set("dateFrom", query.dateFrom);
  if (query.dateTo) params.set("dateTo", query.dateTo);

  const qs = params.toString();
  const url = qs
    ? `${API_BASE}/app-settings/history?${qs}`
    : `${API_BASE}/app-settings/history`;

  const res = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<AppSettingsHistoryResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load settings history";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<AppSettingsHistoryResponse>).data;
}
